/**
 * CAT Edit Page
 *
 * Page for editing an existing CAT record.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { CatForm } from './components';
import {
  Cat,
  CatFormData,
  CatReportType,
  AccidentType,
  CatIssuer,
  AccidentLocationType,
  AffectedBodyPart,
  Laterality,
  InjuryNature,
  UpdateCatRequest,
  canCatBeEdited
} from '../../types/cat';
import { catService } from '../../services/catService';
import { companyService } from '../../services/companyService';
import { employeeService } from '../../services/employeeService';

const CatEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [cat, setCat] = useState<Cat | null>(null);
  const [formData, setFormData] = useState<CatFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // External data for selects
  const [companies, setCompanies] = useState<Array<{ id: number; tradeName: string; legalName: string }>>([]);
  const [employees, setEmployees] = useState<Array<{ id: number; name: string; cpf: string }>>([]);
  const [cidCodes] = useState<Array<{ id: number; code: string; description: string }>>([]);
  const [companyAddress, setCompanyAddress] = useState<{
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    ibgeCode?: string;
  }>();

  useEffect(() => {
    if (id) {
      loadCat(parseInt(id, 10));
    }
  }, [id]);

  const loadCat = async (catId: number) => {
    setLoading(true);
    try {
      const [catData, companiesResponse] = await Promise.all([
        catService.getById(catId),
        companyService.getCompanies({ limit: 1000 })
      ]);

      setCat(catData);
      const mappedCompanies = (companiesResponse.data ?? []).map(c => ({
        id: Number(c.id),
        tradeName: c.tradeName || c.legalName,
        legalName: c.legalName
      }));
      setCompanies(mappedCompanies);

      // Check if CAT can be edited
      if (!canCatBeEdited(catData.status)) {
        enqueueSnackbar('Esta CAT não pode ser editada', { variant: 'warning' });
        navigate(`/cats/${catId}`);
        return;
      }

      // Load employees and company address
      if (catData.companyId) {
        await loadEmployees(catData.companyId);
        try {
          const company = await companyService.getCompanyById(String(catData.companyId));
          setCompanyAddress({
            address: company.address,
            addressNumber: company.addressNumber,
            neighborhood: company.neighborhood,
            zipCode: company.zipCode,
            city: company.city,
            state: company.state,
            ibgeCode: company.ibgeCode,
          });
        } catch {
          setCompanyAddress(undefined);
        }
      }

      // Convert CAT to form data
      const form: CatFormData = {
        employeeId: catData.employeeId,
        companyId: catData.companyId,
        reportType: catData.reportType,
        accidentType: catData.accidentType,
        accidentDate: catData.accidentDate,
        accidentTime: catData.accidentTime ?? '',
        hoursWorkedBefore: catData.hoursWorkedBefore ?? '',
        generatingSituation: catData.generatingSituation,
        issuer: catData.issuer,
        isDeathReport: catData.isDeathReport,
        accidentLocationType: catData.accidentLocationType,
        streetType: catData.streetType ?? '',
        streetName: catData.streetName ?? '',
        streetNumber: catData.streetNumber ?? '',
        complement: catData.complement ?? '',
        neighborhood: catData.neighborhood ?? '',
        zipCode: catData.zipCode ?? '',
        cityCode: catData.cityCode ?? '',
        state: catData.state ?? '',
        countryCode: catData.countryCode ?? '1058',
        affectedBodyPart: catData.affectedBodyPart,
        laterality: catData.laterality ?? '',
        injuryNature: catData.injuryNature,
        injuryDescription: catData.injuryDescription ?? '',
        causingAgent: catData.causingAgent,
        codParteAting: catData.codParteAting ?? '',
        codNatLesao: catData.codNatLesao ?? '',
        codAgntCausador: catData.codAgntCausador ?? '',
        codSitGeradora: catData.codSitGeradora ?? '',
        probableDiagnosis: catData.probableDiagnosis ?? '',
        cidCodeId: catData.cidCodeId ?? '',
        treatmentDate: catData.treatmentDate ?? '',
        treatmentTime: catData.treatmentTime ?? '',
        wasHospitalized: catData.wasHospitalized ?? 'N',
        treatmentDuration: catData.treatmentDuration ?? '',
        willBeAbsent: catData.willBeAbsent ?? 'N',
        issuerName: catData.issuerName ?? '',
        classEntity: catData.classEntity ?? '',
        classEntityNumber: catData.classEntityNumber ?? '',
        classEntityState: catData.classEntityState ?? '',
        sendToEsocial: catData.sendToEsocial,
        esocialEnvironment: catData.esocialEnvironment ?? '',
        notes: catData.notes ?? '',
      };

      setFormData(form);
    } catch (err: unknown) {
      enqueueSnackbar('Erro ao carregar CAT', { variant: 'error' });
      navigate('/cats');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async (companyId: number) => {
    try {
      const data = await employeeService.getEmployeesByCompany(companyId);
      setEmployees(data ?? []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const handleFormChange = (data: Partial<CatFormData>) => {
    if (!formData) return;
    setFormData(prev => prev ? { ...prev, ...data } : null);
    // Clear errors for changed fields
    const changedKeys = Object.keys(data);
    setErrors(prev => {
      const newErrors = { ...prev };
      changedKeys.forEach(key => delete newErrors[key]);
      return newErrors;
    });
  };

  const handleCompanyChange = async (companyId: number) => {
    setEmployees([]);
    if (formData) {
      setFormData(prev => prev ? { ...prev, employeeId: '' } : null);
    }
    loadEmployees(companyId);

    try {
      const company = await companyService.getCompanyById(String(companyId));
      setCompanyAddress({
        address: company.address,
        addressNumber: company.addressNumber,
        neighborhood: company.neighborhood,
        zipCode: company.zipCode,
        city: company.city,
        state: company.state,
        ibgeCode: company.ibgeCode,
      });
    } catch {
      setCompanyAddress(undefined);
    }
  };

  const validateForm = (): boolean => {
    if (!formData) return false;

    const newErrors: Record<string, string> = {};

    if (!formData.companyId) {
      newErrors.companyId = 'Empresa é obrigatória';
    }
    if (!formData.employeeId) {
      newErrors.employeeId = 'Funcionário é obrigatório';
    }
    if (!formData.reportType) {
      newErrors.reportType = 'Tipo de CAT é obrigatório';
    }
    if (!formData.accidentType) {
      newErrors.accidentType = 'Tipo de acidente é obrigatório';
    }
    if (!formData.accidentDate) {
      newErrors.accidentDate = 'Data do acidente é obrigatória';
    }
    if (!formData.generatingSituation) {
      newErrors.generatingSituation = 'Situação geradora é obrigatória';
    }
    if (!formData.issuer) {
      newErrors.issuer = 'Emissor é obrigatório';
    }
    if (!formData.accidentLocationType) {
      newErrors.accidentLocationType = 'Tipo de local é obrigatório';
    }
    if (!formData.codParteAting) {
      newErrors.affectedBodyPart = 'Parte do corpo atingida é obrigatória (Tabela 13)';
    }
    if (!formData.codNatLesao) {
      newErrors.injuryNature = 'Natureza da lesão é obrigatória (Tabela 17)';
    }
    if (!formData.codAgntCausador) {
      newErrors.causingAgent = 'Agente causador é obrigatório (Tabela 14)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!formData || !id) return;

    if (!validateForm()) {
      enqueueSnackbar('Preencha todos os campos obrigatórios', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // Convert form data to API request
      const request: UpdateCatRequest = {
        employeeId: formData.employeeId as number,
        companyId: formData.companyId as number,
        reportType: formData.reportType as CatReportType,
        accidentType: formData.accidentType as AccidentType,
        accidentDate: formData.accidentDate,
        accidentTime: formData.accidentTime ?? undefined,
        hoursWorkedBefore: formData.hoursWorkedBefore ?? undefined,
        generatingSituation: formData.generatingSituation,
        issuer: formData.issuer as CatIssuer,
        isDeathReport: formData.isDeathReport,
        accidentLocationType: formData.accidentLocationType as AccidentLocationType,
        streetType: formData.streetType ?? undefined,
        streetName: formData.streetName ?? undefined,
        streetNumber: formData.streetNumber ?? undefined,
        complement: formData.complement ?? undefined,
        neighborhood: formData.neighborhood ?? undefined,
        zipCode: formData.zipCode ?? undefined,
        cityCode: formData.cityCode ?? undefined,
        state: formData.state ?? undefined,
        countryCode: formData.countryCode ?? undefined,
        affectedBodyPart: (formData.affectedBodyPart || undefined) as AffectedBodyPart | undefined,
        laterality: (formData.laterality || undefined) as Laterality | undefined,
        injuryNature: (formData.injuryNature || undefined) as InjuryNature | undefined,
        injuryDescription: formData.injuryDescription ?? undefined,
        causingAgent: formData.causingAgent,
        probableDiagnosis: formData.probableDiagnosis ?? undefined,
        cidCodeId: formData.cidCodeId as number ?? undefined,
        treatmentDate: formData.treatmentDate ?? undefined,
        treatmentTime: formData.treatmentTime ?? undefined,
        wasHospitalized: formData.wasHospitalized ?? undefined,
        treatmentDuration: formData.treatmentDuration as number ?? undefined,
        willBeAbsent: formData.willBeAbsent ?? undefined,
        issuerName: formData.issuerName ?? undefined,
        classEntity: formData.classEntity ?? undefined,
        classEntityNumber: formData.classEntityNumber ?? undefined,
        classEntityState: formData.classEntityState ?? undefined,
        sendToEsocial: formData.sendToEsocial,
        esocialEnvironment: formData.esocialEnvironment ?? undefined,
        notes: formData.notes ?? undefined,
        codParteAting: formData.codParteAting ?? undefined,
        codNatLesao: formData.codNatLesao ?? undefined,
        codAgntCausador: formData.codAgntCausador ?? undefined,
        codSitGeradora: formData.codSitGeradora ?? undefined,
      };

      await catService.update(parseInt(id, 10), request);
      enqueueSnackbar('CAT atualizada com sucesso', { variant: 'success' });
      navigate(`/cats/${id}`);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao atualizar CAT', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'CAT' },
    { label: cat?.employee?.name || `CAT #${id}` },
    { label: 'Editar' },
  ];

  if (loading || !formData) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} title="Editar CAT">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="Editar CAT"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/cats/${id}`)}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </>
      }
    >
      {/* Form */}
      <Card>
        <CardContent>
          <CatForm
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            companies={companies}
            employees={employees}
            cidCodes={cidCodes}
            companyAddress={companyAddress}
            onCompanyChange={handleCompanyChange}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default CatEditPage;
